import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { carModelsApi, CarModel, CreateCarModelDto, TechProfilesResponse } from '../api/carModelsApi';
import { PaginatedResponse } from '@/shared/types/api';
import { queryKeys, invalidateEntityQueries } from '@/shared/lib/react-query';

// Deprecated: используйте queryKeys.carModels вместо этого
export const CAR_MODELS_QUERY_KEY = ['car-models'];
export const CAR_MODELS_PER_PAGE = 10;

type CarModelsResponse = PaginatedResponse<CarModel>;

export function useCarModels(page: number = 1) {
  return useQuery<CarModelsResponse>({
    queryKey: queryKeys.carModels.list(page),
    queryFn: () => carModelsApi.getCarModels({
      offset: (page - 1) * CAR_MODELS_PER_PAGE,
      limit: CAR_MODELS_PER_PAGE
    })
  });
}

export function useCarModel(id: string) {
  return useQuery({
    queryKey: queryKeys.carModels.detail(id),
    queryFn: () => carModelsApi.getCarModelById(id),
    staleTime: 5 * 60 * 1000, // Кэшируем модели на 5 минут
  });
}

export function useTechProfiles() {
  return useQuery<TechProfilesResponse>({
    queryKey: queryKeys.techProfiles.list(),
    queryFn: () => carModelsApi.getTechProfiles(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCarModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data, image }: { data: CreateCarModelDto; image?: File }) => {
      const model = await carModelsApi.createCarModel(data);
      if (image) {
        await carModelsApi.uploadCarModelImage(model.id, image);
      }
      return model;
    },
    onSuccess: () => {
      invalidateEntityQueries.carModels(queryClient);
    },
  });
}

export function useUpdateCarModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CarModel> }) =>
      carModelsApi.updateCarModel(id, data),
    onSuccess: (_, { id }) => {
      // Инвалидируем конкретную модель и весь список
      queryClient.invalidateQueries({ queryKey: queryKeys.carModels.detail(id) });
      invalidateEntityQueries.carModels(queryClient);
    },
  });
}

export function useDeleteCarModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => carModelsApi.deleteCarModel(id),
    onSuccess: () => {
      invalidateEntityQueries.carModels(queryClient);
    },
  });
}

export function useToggleCarModelActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      carModelsApi.toggleCarModelActive(id, isActive),
    onSuccess: (_, { id }) => {
      // Инвалидируем конкретную модель и весь список
      queryClient.invalidateQueries({ queryKey: queryKeys.carModels.detail(id) });
      invalidateEntityQueries.carModels(queryClient);
    },
  });
}

export function useUploadCarModelImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      carModelsApi.uploadCarModelImage(id, file),
    onSuccess: (_, { id }) => {
      // Инвалидируем конкретную модель и весь список
      queryClient.invalidateQueries({ queryKey: queryKeys.carModels.detail(id) });
      invalidateEntityQueries.carModels(queryClient);
    },
  });
}
